// src/schemas/time.ts
import { z } from "zod";
import type { TimeEntryType } from "@/lib/enums";
import { MAX_ENTRY_HOURS, MIN_ENTRY_HOURS } from "@/lib/time/constants";
import { type IsoDate, isIsoDate, isSunday, parseWeekParam } from "@/lib/time/dates";
import { isQuarterHour } from "@/lib/time/rules";
import type { ReasonLike, SignOffLike } from "@/lib/time/signoff-rules";
import { parseDayParam } from "@/lib/time/today";

/**
 * Time entry wire contract. Inputs are Zod (the server parses every body with them; the
 * person is never in a body, it comes from the session). Responses are plain interfaces the
 * server builds and the hooks read.
 */

const HOURS_RANGE = "Hours must be 0.25 to 24";

export const isoDateSchema = z.string().refine(isIsoDate, "Use a date like 2026-10-15");
// Warehouse ids are upper-case NEWID() values; z.guid() accepts any 8-4-4-4-12 hex id.
const idSchema = z.guid("Unknown id");
export const hoursSchema = z
  .number(HOURS_RANGE)
  .min(MIN_ENTRY_HOURS, HOURS_RANGE)
  .max(MAX_ENTRY_HOURS, HOURS_RANGE)
  .refine(isQuarterHour, "Use quarter hours");
const commentSchema = z.string().max(4000, "Comment is too long").nullable();
/** The entry's ChangedOn as the page loaded it (ISO 127 text), the concurrency guard. */
const changedOnSchema = z.string().max(40).nullable();

export const weekQuerySchema = z.strictObject({
  start: isoDateSchema.refine(isSunday, "Weeks start on Sunday"),
});

/** The Entries Month view: `month` is the 1st of the month, never a person. */
export const monthEntriesQuerySchema = z.strictObject({
  month: isoDateSchema.refine((d) => d.endsWith("-01"), "Months start on the 1st"),
});

export const createEntryInputSchema = z.strictObject({
  /** Made in the browser when the form opens, so a retry hits the primary key. */
  id: z.uuid("Unknown id"),
  projectTaskId: idSchema,
  demandPersonId: idSchema,
  date: isoDateSchema,
  hours: hoursSchema,
  comment: commentSchema,
});
export type CreateEntryInput = z.infer<typeof createEntryInputSchema>;

export const updateEntryInputSchema = z
  .strictObject({
    id: idSchema,
    date: isoDateSchema,
    hours: hoursSchema,
    comment: commentSchema,
    openedChangedOn: changedOnSchema,
    /** Move the entry to another task: both, the task and the assignment it is logged under. */
    projectTaskId: idSchema.optional(),
    demandPersonId: idSchema.optional(),
  })
  .refine((v) => (v.projectTaskId === undefined) === (v.demandPersonId === undefined), {
    message: "Pick a task",
    path: ["projectTaskId"],
  });
export type UpdateEntryInput = z.infer<typeof updateEntryInputSchema>;

export const deleteEntryInputSchema = z.strictObject({
  id: idSchema,
  openedChangedOn: changedOnSchema,
});
export type DeleteEntryInput = z.infer<typeof deleteEntryInputSchema>;

export const setFavoriteInputSchema = z.strictObject({
  projectTaskId: idSchema,
  isFavorite: z.boolean(),
});
export type SetFavoriteInput = z.infer<typeof setFavoriteInputSchema>;

/** Star or unstar a whole project (its tasks, later ones included). */
export const setProjectFavoriteInputSchema = z.strictObject({
  projectId: idSchema,
  isFavorite: z.boolean(),
});
export type SetProjectFavoriteInput = z.infer<typeof setProjectFavoriteInputSchema>;

/**
 * Assignable-task search. The warehouse returns ~1200 rows per person, so the server filters
 * by this text and caps the page; the browser never pulls the whole list per keystroke.
 */
export const assignableQuerySchema = z.strictObject({
  q: z.string().trim().max(100, "Search is too long").optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  /** Only this project's tasks (the Favorites window opening one project). */
  projectId: idSchema.optional(),
  /** Only this task's assignments (a shared task link, `?task=` on the Time page). */
  projectTaskId: idSchema.optional(),
});
export type AssignableQuery = z.infer<typeof assignableQuerySchema>;

export const submitSignOffInputSchema = z.strictObject({
  signOffId: idSchema,
  reasonId: idSchema.nullable(),
  comment: z.string().trim().max(254, "Comment is too long").nullable(),
});
export type SubmitSignOffInput = z.infer<typeof submitSignOffInputSchema>;

/** TE-46: the person revokes one of their own sign-offs; the row id is all the browser sends. */
export const revokeSignOffInputSchema = z.strictObject({ signOffId: idSchema });
export type RevokeSignOffInput = z.infer<typeof revokeSignOffInputSchema>;

export interface TimeEntryView {
  id: string;
  date: IsoDate;
  hours: number;
  comment: string | null;
  projectTaskId: string | null;
  taskName: string;
  projectId: string | null;
  projectName: string | null;
  demandPersonId: string | null;
  type: TimeEntryType;
  taskIsBillable: boolean;
  projectIsBillable: boolean;
  isPto: boolean;
  isInvoiced: boolean;
  monthClosed: boolean;
  periodSignedOff: boolean;
  changedOn: string | null;
}

/** One (task, assignment) the person can log to (rule 2, TE-05). */
export interface AssignableTask {
  key: string;
  projectTaskId: string;
  taskName: string;
  taskIsBillable: boolean;
  projectName: string;
  projectIsBillable: boolean;
  demandPersonId: string;
  demandId: string;
  demandName: string | null;
  accountName: string | null;
  type: TimeEntryType;
  /** On the person's grid: its own star, or its project starred and it not taken off. */
  isFavorite: boolean;
  /** The task's own (warehouse) star. */
  taskIsFavorite: boolean;
  /** Null until the gateway serves ProjectID (then project stars are offered). */
  projectId: string | null;
  /** The person starred this task's project. */
  projectIsFavorite: boolean;
}

/** One project the person can log to, for the Favorites window's project list. */
export interface AssignableProject {
  projectId: string;
  projectName: string;
  accountName: string | null;
  type: TimeEntryType;
  taskCount: number;
  /** Tasks on the person's grid (their own star, or the project's). */
  starredCount: number;
  projectIsFavorite: boolean;
}

export interface AssignableProjectsResult {
  /** False when the gateway gives no project ids yet: the window falls back to task search. */
  supported: boolean;
  projects: AssignableProject[];
}

/** One page of an assignable search: the first `limit` matches and how many matched. */
export interface AssignableSearchResult {
  items: AssignableTask[];
  total: number;
}

/** Guidance and month-to-date hours for one assignment in one month. */
export interface PlanRow {
  demandPersonId: string;
  demandId: string;
  demandName: string | null;
  periodId: string;
  monthStart: IsoDate;
  monthEnd: IsoDate;
  guidance: number | null;
  logged: number;
}

export interface PeriodRow {
  periodId: string;
  periodType: "week" | "month";
  start: IsoDate;
  end: IsoDate;
  timeEntryClosed: boolean;
  defaultQuantity: number | null;
  /** The person has signed this period off. */
  signedOff: boolean;
}

/** One calendar month of the person's entries (the Entries Month view). */
export interface MonthEntriesView {
  /** The 1st of the month. */
  month: IsoDate;
  /** Newest date first, then task name, then id (as the week). */
  entries: TimeEntryView[];
}

export interface WeekView {
  weekStart: IsoDate;
  /** Newest date first, then task name, then id. */
  entries: TimeEntryView[];
  /**
   * What the grid can place rows for: favorites, every task the week's entries reference, and
   * every task on a demand with a plan in the display month(s). Never the capped search.
   */
  assignable: AssignableTask[];
  plans: PlanRow[];
  periods: PeriodRow[];
  thresholdPct: number;
}

export interface SignOffRow extends SignOffLike {
  periodId: string;
  signedOffOn: string | null;
  reasonId: string | null;
  comment: string | null;
  monthClosed: boolean;
}

export type MissingReason = ReasonLike;

export interface SignOffView {
  rows: SignOffRow[];
  reasons: MissingReason[];
  /** The session person's schedule (TE-43: only full-time staff explain missing hours). */
  isFullTime: boolean;
}

export interface EntryWriteResult {
  id: string;
  warnings: string[];
}

export interface FavoriteResult {
  projectTaskId: string;
  isFavorite: boolean;
}

export interface ProjectFavoriteResult {
  projectId: string;
  isFavorite: boolean;
}

export interface SignOffSubmitResult {
  id: string;
  isLate: boolean;
}

export interface SignOffRevokeResult {
  id: string;
}

export interface EnsureResult {
  created: number;
}

/** `?task=` that is not a task id: the raw value is dropped, and Log time says the link is bad. */
export const INVALID_TASK_LINK = "invalid";

/** A `?flag=open` that opens a dialog; anything else is dropped. */
const openFlag = z.literal("open").optional().catch(undefined);

/**
 * The Time page URL (UI standard 12: state an agent can deep-link to and reload):
 * `?week=` a Sunday (none = this week), `?day=` a date (the header's Today chip: opens that
 * day's week with the day selected), `?view=entries` (none = Grid), and `?log=open`,
 * `?favorites=open`, `?signoff=open`, `?meetings=open` for the Log time, Favorites and Sign off
 * dialogs and the From calendar sheet. `?log=open&task=` a ProjectTaskID is a shared task link:
 * Log time opens with that task picked, resolved to the signed-in person's own assignment on the
 * server; the link carries no person or assignment, so it works for whoever opens it.
 * An invalid value parses to undefined (the key stays, so the raw value never reaches the page).
 */
export const timeSearchSchema = z.object({
  week: z.preprocess(parseWeekParam, z.string().optional()),
  day: z.preprocess(parseDayParam, z.string().optional()),
  view: z.literal("entries").optional().catch(undefined),
  /** Entries only: `month` lists the whole month the week belongs to; the default is the week. */
  range: z.literal("month").optional().catch(undefined),
  log: openFlag,
  /** With `log=open`: the task the Log time dialog opens on (a copied task link). */
  task: z
    .union([idSchema, z.literal(INVALID_TASK_LINK)])
    .optional()
    .catch(INVALID_TASK_LINK),
  favorites: openFlag,
  signoff: openFlag,
  /** The From calendar sheet (the week's meetings to accept). */
  meetings: openFlag,
  /** Set by the app when Connect calendar failed: a toast says so once, then it is removed. */
  calendar: z.literal("error").optional().catch(undefined),
});
export type TimeSearch = z.infer<typeof timeSearchSchema>;
